import { useReducer } from 'react';
import { IValidatedState } from '@adac-mp/shared';

export const useValidatedStates = (initState: IValidatedState) => {
	const reducer = (state: IValidatedState, { type, payload }) => {
		switch (type) {
			case 'update':
				return { ...state, [payload.id]: { ...state[payload.id], value: payload.value } };
			case 'updateWithError':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: payload.error,
						warning: false,
					},
				};
			case 'updatePlatesWithConfirmationAndError':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: payload.error,
						warning: false,
						confirmed: payload.confirmed,
						countryCode: payload.countryCode,
						specialPlates: payload.specialPlates,
					},
				};
			case 'updateWithValidation':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: payload.validationFunction(payload.value),
						warning: false,
					},
				};
			case 'updateWithWarning':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: false,
						warning: payload.warning,
					},
				};
			case 'updateAll':
				return {
					...state,
					...payload,
				};
			case 'updateLengthError':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						lengthError: payload.lengthError,
					},
				};
			case 'updateWithoutError':
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: false,
						lengthError: false,
					},
				};
			case 'updateWithConfirmationAndError': {
				return {
					...state,
					[payload.id]: {
						...state[payload.id],
						value: payload.value,
						error: payload.error,
						confirmed: payload.confirmed,
					},
				};
			}
			default:
				return { ...state };
		}
	};
	const [state, dispatch] = useReducer(reducer, initState);

	const update = (id: string, value: string | string[] | boolean | undefined) =>
		dispatch({ type: 'update', payload: { id, value } });

	const updateLengthError = (id: string, lengthError: boolean) =>
		dispatch({ type: 'updateLengthError', payload: { id, lengthError } });

	const updateWithValidation = (
		id: string,
		value: string | string[],
		validationFunction: (val: string | string[]) => boolean,
	) => dispatch({ type: 'updateWithValidation', payload: { id, value, validationFunction } });

	const updateWithError = (id: string, value: string | string[] | boolean | undefined, error: boolean) =>
		dispatch({ type: 'updateWithError', payload: { id, value, error } });

	const updatePlatesWithConfirmationAndError = (
		id: string,
		value: string | string[],
		error: boolean,
		confirmed: boolean,
		countryCode: string,
		specialPlates: boolean,
	) =>
		dispatch({
			type: 'updatePlatesWithConfirmationAndError',
			payload: { id, value, error, confirmed, countryCode, specialPlates },
		});

	const updateWithConfirmationAndError = (id: string, value: string | string[], error: boolean, confirmed: boolean) =>
		dispatch({ type: 'updateWithConfirmationAndError', payload: { id, value, error, confirmed } });

	const updateWithWarning = (id: string, value: string | string[], warning: boolean) =>
		dispatch({ type: 'updateWithWarning', payload: { id, value, warning } });

	const updateWithoutError = (id: string, value: string | string[] | undefined) =>
		dispatch({ type: 'updateWithoutError', payload: { id, value } });

	const updateAll = (payload: IValidatedState) => dispatch({ type: 'updateAll', payload });

	const states: IValidatedState[] = Object.values(state);
	const hasErrors = () => states.find((s: IValidatedState) => s?.error);

	const hasWarnings = () => states.find((s: IValidatedState) => s?.warning);

	return [
		state,
		{
			update,
			updateWithValidation,
			updateWithError,
			updateWithWarning,
			updateAll,
			updateLengthError,
			updateWithoutError,
			updatePlatesWithConfirmationAndError,
			updateWithConfirmationAndError,
			hasErrors,
			hasWarnings,
		},
	] as const;
};
